import React, { FC, useState, useCallback, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  SavePersonalDetailsRequest,
  SavePersonalDetailsResponse,
  CheckDeliveryAreaResponse,
} from '@dreamai/types';
import { AddressFinder } from '@ideal-postcodes/address-finder';
import { ReactComponent as LockIcon } from '../../../icons/lock-outline.svg';
import api from '../../../api';
import { OnboardingStepProps } from '../OnboardingFlow';
import {
  formatUkDateInput,
  parseUkDateToIso,
  validateUkDateOfBirth,
} from '../../../utils/ukDate';
import './personal-details-step.scss';

// Preload the shrouded dreamie image so it's cached by the time the user reaches SetPersonalityStep
const SHROUDED_DREAMIE_URL =
  'https://d12b9vnf4u4q7j.cloudfront.net/dreamies/shrouded-dreamie.png';
if (typeof window !== 'undefined') {
  const img = new Image();
  img.src = SHROUDED_DREAMIE_URL;
}

const PersonalDetailsStep: FC<OnboardingStepProps> = ({ refreshStatus }) => {
  const { getAccessTokenSilently } = useAuth0();

  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState(''); // UK format DD/MM/YYYY in UI
  const [referralCode, setReferralCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Ideal Postcodes Address Finder — input is uncontrolled so the widget can manage it
  const addressFinderInit = useRef(false);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [deliveryAreaChecked, setDeliveryAreaChecked] = useState(false);
  const [deliveryAreaSupported, setDeliveryAreaSupported] = useState<
    boolean | null
  >(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCheckedRef = useRef('');

  const checkDeliveryArea = useCallback(
    async (address: string) => {
      if (!address.trim() || address.trim().length < 5) return;
      if (address.trim() === lastCheckedRef.current) return;
      lastCheckedRef.current = address.trim();
      try {
        const token = await getAccessTokenSilently();
        const { data } = await api.get<CheckDeliveryAreaResponse>(
          '/onboarding/check-delivery-area',
          {
            params: { address },
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setDeliveryAreaSupported(data.supported);
      } catch (err) {
        console.error('[PersonalDetails] Delivery area check error:', err);
        setDeliveryAreaSupported(null);
      }
      setDeliveryAreaChecked(true);
    },
    [getAccessTokenSilently],
  );

  useEffect(() => {
    if (addressFinderInit.current) return;
    addressFinderInit.current = true;

    const apiKey = process.env.REACT_APP_IDEAL_POSTCODES_API_KEY;
    if (!apiKey) return;

    AddressFinder.setup({
      apiKey,
      inputField: '#address-input',
      outputFields: {},
      detectCountry: false,
      defaultCountry: 'GBR',
      restrictCountries: ['GBR'],
      onAddressRetrieved: (address: any) => {
        const formatted = [
          address.line_1,
          address.line_2,
          address.line_3,
          address.post_town,
          address.postcode,
        ]
          .filter((part) => part && part !== '')
          .join(', ');
        if (addressInputRef.current) {
          addressInputRef.current.value = formatted;
        }
        setSelectedAddress(formatted);
        setDeliveryAreaChecked(false);
        setDeliveryAreaSupported(null);
        // Immediate check — user made a definitive selection
        if (debounceRef.current) clearTimeout(debounceRef.current);
        checkDeliveryArea(formatted);
      },
    });
  }, [checkDeliveryArea]);

  // Debounced delivery area check on manual typing (1.5s idle)
  useEffect(() => {
    const input = addressInputRef.current;
    if (!input) return;

    const handleInput = () => {
      setDeliveryAreaChecked(false);
      setDeliveryAreaSupported(null);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const value = input.value?.trim() || '';
        if (value) {
          setSelectedAddress(value);
          checkDeliveryArea(value);
        }
      }, 1500);
    };

    input.addEventListener('input', handleInput);
    return () => {
      input.removeEventListener('input', handleInput);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [checkDeliveryArea]);

  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) {
      newErrors.name = 'You need to enter a name';
    }
    const addr =
      selectedAddress || addressInputRef.current?.value?.trim() || '';
    if (!addr) {
      newErrors.address = 'Please select an address from the suggestions';
    }
    const trimmedPhone = phoneNumber.trim();
    if (!trimmedPhone) {
      newErrors.phoneNumber = 'A phone number is required';
    } else if (trimmedPhone.replace(/\D/g, '').length < 7) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }
    const dobErr = validateUkDateOfBirth(dateOfBirth);
    if (dobErr) {
      newErrors.dateOfBirth = dobErr;
    }
    if (!referralCode.trim()) {
      newErrors.referralCode = 'A referral code is required to sign up';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [name, selectedAddress, phoneNumber, dateOfBirth, referralCode]);

  const handleSubmit = useCallback(async () => {
    if (!validate()) return;

    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const location =
        selectedAddress || addressInputRef.current?.value?.trim() || '';
      const isoDob = parseUkDateToIso(dateOfBirth.trim());
      if (!isoDob) {
        // validate() already guarded this, but TypeScript needs the narrowing
        setErrors((prev) => ({
          ...prev,
          dateOfBirth: 'Please enter a valid date as DD/MM/YYYY',
        }));
        setSubmitting(false);
        return;
      }
      const body: SavePersonalDetailsRequest = {
        name: name.trim(),
        location,
        referralCode: referralCode.trim(),
        phoneNumber: phoneNumber.trim(),
        dateOfBirth: isoDob,
        isWorkEmail: false,
      };

      await api.post<SavePersonalDetailsResponse>(
        '/onboarding/personal-details',
        body,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      sessionStorage.setItem('onboarding_userName', name.trim().split(' ')[0]);

      await refreshStatus();
    } catch (err: any) {
      const msg =
        err?.response?.data?.error || 'Something went wrong. Please try again.';
      setErrors({ submit: msg });
    } finally {
      setSubmitting(false);
    }
  }, [
    name,
    phoneNumber,
    dateOfBirth,
    referralCode,
    selectedAddress,
    validate,
    getAccessTokenSilently,
    refreshStatus,
  ]);

  return (
    <div className='onboarding-step personal-details-step'>
      <div className='step-header'>
        <h1>Setup your account</h1>
      </div>

      <div className='step-inner'>
        <div className='section-intro'>
          <p className='section-title'>About you</p>
          <p className='section-description'>
            Add your details so we can introduce you to your new assistant.
          </p>
        </div>

        <div className='field'>
          <label className='field-label'>Your full name</label>
          <input
            type='text'
            className={`field-input ${errors.name ? 'has-error' : ''}`}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) setErrors((prev) => ({ ...prev, name: '' }));
            }}
            placeholder=''
          />
          {errors.name && <p className='field-error'>{errors.name}</p>}
        </div>

        <div className='field address-field'>
          <label className='field-label'>Your address</label>
          <p className='field-hint'>
            Type the first line of your address to search
          </p>
          <input
            ref={addressInputRef}
            id='address-input'
            type='text'
            className={`field-input${errors.address ? ' has-error' : ''}`}
            placeholder=''
            autoComplete='off'
          />
          {errors.address && <p className='field-error'>{errors.address}</p>}
          {deliveryAreaChecked && deliveryAreaSupported === false && (
            <p className='delivery-area-hint'>
              We're still rolling out some real-world tasks like collecting
              laundry or picking up coffee. Once they're available in your area
              we'll let you know.
            </p>
          )}
        </div>

        <div className='field'>
          <div className='field-label-group'>
            <label className='field-label'>Phone number</label>
            <p className='field-hint'>
              I'll use this if it's needed when booking things for you
            </p>
          </div>
          <input
            type='tel'
            className={`field-input ${errors.phoneNumber ? 'has-error' : ''}`}
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber(e.target.value);
              if (errors.phoneNumber)
                setErrors((prev) => ({ ...prev, phoneNumber: '' }));
            }}
            placeholder=''
          />
          {errors.phoneNumber && (
            <p className='field-error'>{errors.phoneNumber}</p>
          )}
        </div>

        <div className='field'>
          <div className='field-label-group'>
            <label className='field-label'>Date of birth</label>
            <p className='field-hint'>
              Used for things like ID checks and bookings. Format: DD/MM/YYYY
            </p>
          </div>
          <input
            type='text'
            inputMode='numeric'
            autoComplete='bday'
            className={`field-input ${errors.dateOfBirth ? 'has-error' : ''}`}
            value={dateOfBirth}
            onChange={(e) => {
              setDateOfBirth(formatUkDateInput(e.target.value));
              if (errors.dateOfBirth)
                setErrors((prev) => ({ ...prev, dateOfBirth: '' }));
            }}
            placeholder='DD/MM/YYYY'
            maxLength={10}
          />
          {errors.dateOfBirth && (
            <p className='field-error'>{errors.dateOfBirth}</p>
          )}
        </div>

        <div className='field'>
          <div className='field-label-group'>
            <div className='field-label-with-icon'>
              <LockIcon />
              <label className='field-label'>Sign-up code</label>
            </div>
            <p className='field-hint'>
              You need a referral code to join Dream, add it here
            </p>
          </div>
          <input
            type='text'
            className={`field-input ${errors.referralCode ? 'has-error' : ''}`}
            value={referralCode}
            onChange={(e) => {
              setReferralCode(e.target.value);
              if (errors.referralCode)
                setErrors((prev) => ({ ...prev, referralCode: '' }));
            }}
            placeholder=''
          />
          {errors.referralCode && (
            <p className='field-error'>{errors.referralCode}</p>
          )}
        </div>

        {errors.submit && <p className='submit-error'>{errors.submit}</p>}
      </div>

      <div className='step-footer'>
        <button
          className='step-button primary-gradient'
          onClick={handleSubmit}
          disabled={submitting}
        >
          <span>{submitting ? 'Saving...' : 'Next'}</span>
          {!submitting && (
            <svg
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
            >
              <circle cx='12' cy='12' r='9' />
              <path d='M12 8l4 4-4 4M8 12h8' />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};

export default PersonalDetailsStep;
